import { Box, Tooltip, styled, IconButton } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import Agua from "@icons-rf/properties-item/agua.svg?react";
import Bath from "@icons-rf/properties-item/bath.svg?react";
import Bed from "@icons-rf/properties-item/bed.svg?react";
import FloorsTotal from "@icons-rf/properties-item/floors-total.svg?react";
import Carro from "@icons-rf/properties-item/carro.svg?react";
import Casa2 from "@icons-rf/properties-item/casa2.svg?react";
import HalfBath from "@icons-rf/properties-item/toiled_blue.png";
import Measure from "@icons-rf/properties-item/medida.svg?react";
import Pump from "@icons-rf/properties-item/pumps.svg?react";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import MoneyFormat from "@/src-refactor/modules/shared/helpers/moneyFormat.helper";
import { IPrepareFeatureArg } from "../../../../../helpers/property/prepare-feature";
import { Button } from "@mui/base";
import MxTooltipNormal from "@rf-l1/tooltip/MxTooltipNormal";

type IMxCPropertyFeatureProps = IPrepareFeatureArg;

const ScrollContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  width: "100%",
  overflow: "hidden"
}));

const ScrollContent = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(2),
  overflowX: "auto",
  scrollBehavior: "smooth",
  "&::-webkit-scrollbar": {
    display: "none"
  },
  msOverflowStyle: "none",
  scrollbarWidth: "none"
}));

const ScrollArrow = styled(Button)(({ theme }) => ({
  position: "absolute",
  top: "50%",
  zIndex: 1,
  transform: "translate(0, -50%)",
  backgroundColor: "rgba(255, 255, 255, 0.7)",
  borderRadius: 0,
  boxShadow: "none",
  border: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  "& svg": {
    fontSize: 14,
    color: theme.palette.primary.main
  }
}));

const FeatureItem = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  minWidth: "fit-content",
  "& svg": {
    width: 15,
    height: 15,
    color: theme.palette.primary.main
  },
  "& img": {
    width: 15,
    height: 15,
    objectFit: "contain;",
    color: theme.palette.primary.main
  },
  "& span": {
    fontSize: 10,
    color: theme.palette.primary.main
  }
}));

function MxCPropertyFeature(props: IMxCPropertyFeatureProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [showScrollButtons, setShowScrollButtons] = useState(false);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);

  const checkScrollPosition = useCallback(() => {
    if (contentRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = contentRef.current;
      // Show left arrow only if not at the beginning
      setShowLeftArrow(scrollLeft > 0);
      // Show right arrow only if not at the end
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 5); // 5px buffer for rounding errors
    }
  }, []);

  useEffect(() => {
    const checkScrollability = () => {
      if (contentRef.current) {
        const hasOverflow =
          contentRef.current.scrollWidth > contentRef.current.clientWidth;
        setShowScrollButtons(hasOverflow);
        if (hasOverflow) {
          checkScrollPosition();
        }
      }
    };

    checkScrollability();
    window.addEventListener("resize", checkScrollability);

    return () => {
      window.removeEventListener("resize", checkScrollability);
    };
  }, [checkScrollPosition]);

  useEffect(() => {
    const scrollElement = contentRef.current;
    if (scrollElement) {
      scrollElement.addEventListener("scroll", checkScrollPosition);
      return () => {
        scrollElement.removeEventListener("scroll", checkScrollPosition);
      };
    }
  }, [checkScrollPosition]);

  const handleScroll = (direction: "left" | "right") => {
    if (contentRef.current) {
      const scrollAmount = 150;
      const currentScroll = contentRef.current.scrollLeft;
      contentRef.current.scrollLeft =
        direction === "left"
          ? currentScroll - scrollAmount
          : currentScroll + scrollAmount;
    }
  };

  const orderedItems = [
    {
      icon: Bed,
      value: props?.bedrooms,
      label: "Habitaciones"
    },
    {
      icon: Bath,
      value: props?.bathrooms,
      label: "Baños"
    },
    {
      img: HalfBath,
      value: props?.halfBathrooms,
      label: "Medios baños"
    },
    {
      icon: Carro,
      value: props?.parkingSpots,
      label: "Parqueo"
    },
    {
      icon: Agua,
      value: props?.floorLevel,
      label: "Nivel de piso"
    },
    {
      icon: FloorsTotal,
      value: props?.floorsTotal,
      label: "Total de pisos"
    },
    {
      icon: Casa2,
      value: props.sqmLand ? MoneyFormat(props.sqmLand) : null,
      label: "Metros de Solar",
      sub: true
    },
    {
      icon: Measure,
      value: props.sqmConstruction ? MoneyFormat(props.sqmConstruction) : null,
      label: "Metros de Construcción",
      sub: true
    }
  ];

  const extraItems = [
    {
      icon: Pump,
      value: props?.pumps,
      label: "Bombas"
    }
  ];

  const items = [...orderedItems, ...extraItems].filter(item => item.value);

  return (
    <ScrollContainer>
      {showScrollButtons && showLeftArrow && (
        <ScrollArrow
          onClick={() => handleScroll("left")}
          sx={{ left: -10 }}
          size="small"
        >
          <ArrowBackIosNewIcon />
        </ScrollArrow>
      )}
      <ScrollContent ref={contentRef}>
        <div />
        {items.map(item => (
          <MxTooltipNormal key={item.label} label={item.label} placement="bottom">
            <FeatureItem>
              {item?.icon && <item.icon />}
              {item?.img && <img src={item.img} />}
              <span>
                {item.value}
                {item.sub && (
                  <span>
                    mt<sup>2</sup>
                  </span>
                )}
              </span>
            </FeatureItem>
          </MxTooltipNormal>
        ))}
        <div />
      </ScrollContent>
      {showScrollButtons && showRightArrow && (
        <ScrollArrow
          onClick={() => handleScroll("right")}
          sx={{ right: -10 }}
          size="small"
        >
          <ArrowForwardIosIcon />
        </ScrollArrow>
      )}
    </ScrollContainer>
  );
}

export default MxCPropertyFeature;
